import { formatSEK } from '@budgie/shared';
import { type FormEvent, Fragment, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { trpc } from '../../lib/trpc';
import { useConfirm } from '../common/ConfirmProvider';
import { EmptyState } from '../common/EmptyState';
import { TableSkeleton } from '../common/Skeleton';
import { IncomeHistory } from './IncomeHistory';
import { DECIMAL_INPUT_PATTERN, oreToInputValue, parseMoneyInput } from './moneyInput';

const EMPTY_RULES: RuleItem[] = [];

function haveSameIds(left: string[], right: string[]) {
  return left.length === right.length && left.every((id, index) => id === right[index]);
}

type IncomeSource = {
  id: string;
  name: string;
  expectedAmount: number;
  isVariable: boolean;
  memberId: string | null;
  memberDisplayName: string;
  rules: { id: string; pattern: string; matchType: string }[];
  matchedCount: number;
};

type RuleItem = {
  id: string;
  pattern: string;
  categoryId: string;
  linkedIncomeSourceId?: string | null;
  linkedIncomeSourceName?: string | null;
};

type Props = {
  open: boolean;
  source: IncomeSource | null;
  onClose: () => void;
  onSaved: () => void;
};

function IncomeDialog({ open, source, onClose, onSaved }: Props) {
  const utils = trpc.useUtils();
  const membersQuery = trpc.household.get.useQuery();
  const rulesQuery = trpc.categoryRule.list.useQuery(undefined, {
    enabled: open,
    retry: false,
  });
  const categoriesQuery = trpc.category.listGroups.useQuery(undefined, {
    enabled: open,
    retry: false,
  });

  const createMutation = trpc.income.create.useMutation({
    onSuccess: async () => {
      await utils.income.list.invalidate();
      toast.success('Inkomstkälla skapad');
      onSaved();
    },
    onError: (err) => toast.error(err.message),
  });
  const updateMutation = trpc.income.update.useMutation({
    onSuccess: async () => {
      await utils.income.list.invalidate();
      toast.success('Inkomstkälla uppdaterad');
      onSaved();
    },
    onError: (err) => toast.error(err.message),
  });

  const [name, setName] = useState('');
  const [expectedAmount, setExpectedAmount] = useState('0');
  const [isVariable, setIsVariable] = useState(false);
  const [memberId, setMemberId] = useState('shared');
  const [ruleSearch, setRuleSearch] = useState('');
  const [selectedRuleIds, setSelectedRuleIds] = useState<string[]>([]);

  const rules = (rulesQuery.data as RuleItem[] | undefined) ?? EMPTY_RULES;
  const members: { id: string; displayName: string }[] =
    (membersQuery.data as { members?: { id: string; displayName: string }[] } | undefined)
      ?.members ?? [];

  useEffect(() => {
    if (!open) {
      setName('');
      setExpectedAmount('0');
      setIsVariable(false);
      setMemberId('shared');
      setRuleSearch('');
      setSelectedRuleIds([]);
      return;
    }

    setName(source?.name ?? '');
    setExpectedAmount(source ? oreToInputValue(source.expectedAmount) : '0');
    setIsVariable(source?.isVariable ?? false);
    setMemberId(source?.memberId ?? 'shared');
    setRuleSearch('');
  }, [source, open]);

  useEffect(() => {
    if (!open) return;

    if (!source) {
      setSelectedRuleIds((current) => (current.length === 0 ? current : []));
      return;
    }

    const nextSelectedRuleIds = rules
      .filter((rule) => rule.linkedIncomeSourceId === source.id)
      .map((rule) => rule.id);

    setSelectedRuleIds((current) =>
      haveSameIds(current, nextSelectedRuleIds) ? current : nextSelectedRuleIds,
    );
  }, [source, open, rules]);

  const categoryLabels = useMemo(() => {
    const map = new Map<string, string>();
    for (const group of categoriesQuery.data ?? []) {
      for (const category of group.categories) {
        map.set(category.id, `${group.name} / ${category.name}`);
      }
    }
    return map;
  }, [categoriesQuery.data]);

  const filteredRules = useMemo(() => {
    const searchValue = ruleSearch.trim().toLowerCase();
    if (!searchValue) return rules;

    return rules.filter((rule) => {
      const categoryLabel = categoryLabels.get(rule.categoryId)?.toLowerCase() ?? '';
      return (
        rule.pattern.toLowerCase().includes(searchValue) || categoryLabel.includes(searchValue)
      );
    });
  }, [categoryLabels, ruleSearch, rules]);

  const handleToggleRule = (ruleId: string) => {
    setSelectedRuleIds((current) =>
      current.includes(ruleId) ? current.filter((id) => id !== ruleId) : [...current, ruleId],
    );
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();

    if (selectedRuleIds.length === 0) {
      toast.error('Välj minst en regel för att koppla inkomstkällan');
      return;
    }

    const parsedAmount = parseMoneyInput(expectedAmount);
    if (parsedAmount === null) {
      toast.error('Ange ett giltigt belopp, t.ex. 1 250,50.');
      return;
    }

    const payload = {
      name: name.trim(),
      expectedAmount: parsedAmount,
      isVariable,
      memberId: memberId === 'shared' ? null : memberId,
      categoryRuleIds: selectedRuleIds,
    };

    if (source) {
      updateMutation.mutate({ id: source.id, ...payload });
      return;
    }

    createMutation.mutate(payload);
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent className="sm:max-w-2xl" showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>{source ? 'Redigera inkomstkälla' : 'Lägg till inkomstkälla'}</DialogTitle>
          <DialogDescription>
            Koppla en eller flera kategoriregler till inkomstkällan så att importerade transaktioner
            kan matchas automatiskt.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="income-name">Namn</Label>
            <Input
              id="income-name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1"
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="income-expected">Förväntat belopp/mån (kr)</Label>
              <Input
                id="income-expected"
                type="text"
                inputMode="decimal"
                autoComplete="off"
                pattern={DECIMAL_INPUT_PATTERN}
                value={expectedAmount}
                onChange={(e) => setExpectedAmount(e.target.value)}
                className="mt-1 font-mono"
              />
            </div>
            <div>
              <Label htmlFor="income-member">Tillhör</Label>
              <Select value={memberId} onValueChange={(value) => setMemberId(value)}>
                <SelectTrigger id="income-member" className="mt-1 w-full">
                  <SelectValue placeholder="Välj…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="shared">Gemensam</SelectItem>
                  {members.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Switch
              id="income-variable"
              checked={isVariable}
              onCheckedChange={(checked) => setIsVariable(checked === true)}
            />
            <Label htmlFor="income-variable" className="text-sm text-foreground">
              Rörlig inkomst
            </Label>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="income-rule-search">Kopplade regler</Label>
              <span className="text-xs text-muted-foreground">{selectedRuleIds.length} valda</span>
            </div>
            <Input
              id="income-rule-search"
              value={ruleSearch}
              onChange={(event) => setRuleSearch(event.target.value)}
              placeholder="Sök på regel eller kategori"
            />
            <div className="relative max-h-72 space-y-2 overflow-y-auto rounded-xl border border-border p-3">
              {rules.length === 0 ? (
                <p className="text-sm text-muted-foreground">Inga kategoriregler finns ännu.</p>
              ) : filteredRules.length === 0 ? (
                <p className="text-sm text-muted-foreground/70">Inga regler matchar sökningen.</p>
              ) : (
                filteredRules.map((rule) => {
                  const isSelected = selectedRuleIds.includes(rule.id);
                  const isLinkedToOther =
                    rule.linkedIncomeSourceId != null && rule.linkedIncomeSourceId !== source?.id;

                  return (
                    <label
                      key={rule.id}
                      htmlFor={`income-rule-${rule.id}`}
                      className={`flex items-start gap-3 rounded-lg border px-3 py-3 ${isLinkedToOther ? 'border-dashed bg-muted/30 opacity-60' : 'cursor-pointer border-border'}`}
                    >
                      <Checkbox
                        id={`income-rule-${rule.id}`}
                        aria-label={`Koppla regel ${rule.pattern}`}
                        checked={isSelected}
                        disabled={isLinkedToOther}
                        onCheckedChange={() => handleToggleRule(rule.id)}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="font-mono text-sm text-foreground">{rule.pattern}</div>
                        <div className="text-xs text-muted-foreground">
                          {categoryLabels.get(rule.categoryId) ?? 'Okänd kategori'}
                        </div>
                        {isLinkedToOther && rule.linkedIncomeSourceName && (
                          <div className="mt-1 text-xs text-warning">
                            Redan kopplad till "{rule.linkedIncomeSourceName}"
                          </div>
                        )}
                      </div>
                    </label>
                  );
                })
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Avbryt
            </Button>
            <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
              {createMutation.isPending || updateMutation.isPending
                ? 'Sparar…'
                : source
                  ? 'Spara'
                  : 'Lägg till'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function IncomeShiftSettings() {
  const settingsQuery = trpc.household.getSettings.useQuery();
  const utils = trpc.useUtils();
  const updateMutation = trpc.household.updateSettings.useMutation({
    onSuccess: () => {
      void utils.household.getSettings.invalidate();
      toast.success('Inställningar sparade');
    },
    onError: (err) => toast.error(err.message),
  });

  const enabled = settingsQuery.data?.incomeShiftEnabled ?? true;
  const day = settingsQuery.data?.incomeShiftDay ?? 20;

  return (
    <Card className="rounded-2xl p-4 sm:p-5">
      <div className="space-y-3">
        <h2 className="text-[15px] font-semibold">Inkomstperiod</h2>
        <p className="text-xs text-muted-foreground">
          När aktiverat räknas inkomst från vald dag föregående månad till samma dag innevarande
          månad. Exempelvis med dag 25 räknas lön som kommer 25:e som nästa månads inkomst.
        </p>
        <div className="flex items-center gap-3">
          <Switch
            id="income-shift-toggle"
            checked={enabled}
            disabled={settingsQuery.isLoading}
            onCheckedChange={(checked) =>
              updateMutation.mutate({
                incomeShiftEnabled: checked === true,
              })
            }
          />
          <Label htmlFor="income-shift-toggle" className="text-sm text-foreground">
            Förskjuten inkomstperiod
          </Label>
        </div>
        {enabled && (
          <div className="flex items-center gap-3">
            <Label
              htmlFor="income-shift-day"
              className="text-sm text-muted-foreground whitespace-nowrap"
            >
              Brytdag
            </Label>
            <Input
              id="income-shift-day"
              type="number"
              inputMode="numeric"
              min={1}
              max={28}
              className="w-20 font-mono"
              value={day}
              disabled={settingsQuery.isLoading}
              onChange={(e) => {
                const val = Number.parseInt(e.target.value, 10);
                if (val >= 1 && val <= 28) {
                  updateMutation.mutate({
                    incomeShiftDay: val,
                  });
                }
              }}
            />
          </div>
        )}
      </div>
    </Card>
  );
}

export function IncomeList() {
  const { confirm } = useConfirm();
  const query = trpc.income.list.useQuery();
  const utils = trpc.useUtils();
  const deleteMutation = trpc.income.delete.useMutation({
    onSuccess: () => {
      void utils.income.list.invalidate();
      toast.success('Inkomstkälla borttagen');
    },
    onError: (err) => toast.error(err.message),
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSource, setEditingSource] = useState<IncomeSource | null>(null);
  const [selectedSourceId, setSelectedSourceId] = useState<string | null>(null);

  if (query.isLoading) return <TableSkeleton rows={4} columns={3} />;
  if (query.error) return <p className="text-sm text-destructive">{query.error.message}</p>;

  const sources: IncomeSource[] = (query.data ?? []) as IncomeSource[];

  const toggleSource = (id: string) =>
    setSelectedSourceId((current) => (current === id ? null : id));

  const sourceMeta = (src: IncomeSource) =>
    `${src.memberDisplayName}${src.isVariable ? ' · rörlig' : ''}${
      src.matchedCount > 0 ? ` · ${src.matchedCount} matchade` : ''
    }`;

  const renderActions = (src: IncomeSource) => (
    <div className="flex shrink-0 justify-end gap-1">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        aria-label={`Redigera ${src.name}`}
        onClick={(e) => {
          e.stopPropagation();
          setEditingSource(src);
          setDialogOpen(true);
        }}
        className="h-11 px-3 text-xs text-muted-foreground hover:text-foreground sm:h-auto sm:px-2"
      >
        Redigera
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        aria-label={`Ta bort ${src.name}`}
        onClick={async (e) => {
          e.stopPropagation();
          const ok = await confirm({
            title: 'Ta bort inkomstkälla',
            message: `Är du säker på att du vill ta bort "${src.name}"?`,
            confirmLabel: 'Ta bort',
            destructive: true,
          });
          if (ok)
            deleteMutation.mutate({
              id: src.id,
            });
        }}
        className="h-11 px-3 text-xs text-destructive hover:text-destructive/80 sm:h-auto sm:px-2"
      >
        Ta bort
      </Button>
    </div>
  );

  return (
    <div className="space-y-4">
      <IncomeShiftSettings />

      <div className="flex justify-end">
        <Button
          type="button"
          onClick={() => {
            setEditingSource(null);
            setDialogOpen(true);
          }}
        >
          + Lägg till inkomstkälla
        </Button>
      </div>

      <Card className="overflow-hidden rounded-2xl py-0">
        {sources.length === 0 ? (
          <EmptyState
            icon="💰"
            title="Inga inkomstkällor"
            description="Lägg till inkomstkällor för att spåra hushållets inkomster."
            action={{
              label: '+ Lägg till inkomstkälla',
              onClick: () => {
                setEditingSource(null);
                setDialogOpen(true);
              },
            }}
          />
        ) : (
          <>
            {/* Mobile: stacked rows */}
            <ul className="divide-y divide-border md:hidden">
              {sources.map((src) => {
                const isSelected = selectedSourceId === src.id;
                return (
                  <li key={src.id} className={isSelected ? 'bg-muted/40' : undefined}>
                    <button
                      type="button"
                      aria-expanded={isSelected}
                      aria-controls={`income-history-mobile-${src.id}`}
                      onClick={() => toggleSource(src.id)}
                      className="flex min-h-11 w-full items-start gap-3 px-4 pt-3 pb-1 text-left"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="break-words text-sm text-foreground">{src.name}</div>
                        <div className="text-xs text-muted-foreground/70">{sourceMeta(src)}</div>
                      </div>
                      <div className="shrink-0 text-right">
                        <div className="tabular-nums text-sm">{formatSEK(src.expectedAmount)}</div>
                        <div className="text-xs text-muted-foreground">/mån</div>
                      </div>
                    </button>
                    <div className="flex items-center justify-between gap-2 pl-4 pr-2 pb-1">
                      <span className="text-xs text-muted-foreground">
                        {src.rules.length} {src.rules.length === 1 ? 'regel' : 'regler'}
                      </span>
                      {renderActions(src)}
                    </div>
                    {isSelected && (
                      <div id={`income-history-mobile-${src.id}`} className="bg-muted/30 p-4">
                        <IncomeHistory sourceId={src.id} />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>

            {/* Desktop: table */}
            <table className="hidden w-full md:table">
              <thead>
                <tr className="border-b border-border text-xs text-muted-foreground">
                  <th className="py-1.5 pl-4 text-left font-medium">Källa</th>
                  <th className="py-1.5 px-2 text-right font-medium">Förväntat/mån</th>
                  <th className="py-1.5 px-2 text-center font-medium">Regler</th>
                  <th className="py-1.5 pr-4 text-right font-medium">
                    <span className="sr-only">Åtgärder</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sources.map((src) => {
                  const isSelected = selectedSourceId === src.id;
                  return (
                    <Fragment key={src.id}>
                      <tr
                        className={`border-b border-border last:border-0 cursor-pointer ${isSelected ? 'bg-muted/40' : 'hover:bg-muted/50'}`}
                        onClick={() => toggleSource(src.id)}
                      >
                        <td className="py-2 pl-4 pr-2">
                          <button
                            type="button"
                            aria-expanded={isSelected}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleSource(src.id);
                            }}
                            className="text-left text-sm text-foreground"
                          >
                            {src.name}
                          </button>
                          <div className="text-xs text-muted-foreground/70">{sourceMeta(src)}</div>
                        </td>
                        <td className="py-2 px-2 text-right tabular-nums text-sm">
                          {formatSEK(src.expectedAmount)}
                        </td>
                        <td className="py-2 px-2 text-center text-xs text-muted-foreground">
                          {src.rules.length}
                        </td>
                        <td className="py-2 pr-4 text-right">{renderActions(src)}</td>
                      </tr>
                      {isSelected && (
                        <tr>
                          <td colSpan={4} className="bg-muted/30 p-4">
                            <IncomeHistory sourceId={src.id} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </Card>

      {dialogOpen && (
        <IncomeDialog
          open={dialogOpen}
          source={editingSource}
          onClose={() => setDialogOpen(false)}
          onSaved={() => {
            setDialogOpen(false);
            void utils.income.list.invalidate();
          }}
        />
      )}
    </div>
  );
}
